import { supabase } from '@/lib/supabase';
import { functionsBaseUrl, supabaseAnonKey } from '@/lib/supabaseConfig';

const SESSION_KEY = 'analytics_session_id';
let visitRequest: Promise<void> | undefined;
let savedVisitDay = '';

export function trackSiteVisit(): Promise<void> {
  const day = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
  if (savedVisitDay === day) return Promise.resolve();
  if (visitRequest) return visitRequest;
  visitRequest = (async () => {
    try {
      const response = await fetch(`${functionsBaseUrl}/track-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: supabaseAnonKey },
        body: JSON.stringify({ session_id: getOrCreateSessionId(), event_type: 'visit' }),
        keepalive: true,
      });
      if (response.ok && (await response.json()).success) savedVisitDay = day;
    } catch { /* Retry on the next public navigation; never block visitors. */ }
    finally { visitRequest = undefined; }
  })();
  return visitRequest;
}

function getOrCreateSessionId(): string {
  try {
    const existing = localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const newId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, newId);
    return newId;
  } catch {
    return crypto.randomUUID();
  }
}

function generateVisitorHash(): string {
  const parts = [
    navigator.hardwareConcurrency || '',
    navigator.maxTouchPoints || '',
    screen.colorDepth || '',
    screen.width || '',
    screen.height || '',
    new Date().getTimezoneOffset(),
    navigator.language || '',
  ];
  return parts.join('|');
}

function parseBrowser(): { browser: string; os: string; device: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/')) browser = 'Safari';

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) { os = 'Android'; device = 'Mobile'; }
  else if (ua.includes('iPhone') || ua.includes('iPad')) { os = 'iOS'; device = ua.includes('iPad') ? 'Tablet' : 'Mobile'; }

  return { browser, os, device };
}

function getSessionMeta(): Record<string, string> {
  const { browser, os, device } = parseBrowser();
  return {
    visitor_hash: generateVisitorHash(),
    device,
    browser,
    operating_system: os,
    referrer: document.referrer || '',
    landing_page: window.location.pathname,
    utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
    utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
    utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
  };
}

let sessionMetaSent = false;

async function trackEvent(propertyId: number, eventType: string): Promise<void> {
  const sessionId = getOrCreateSessionId();

  try {
    const body: any = {
      session_id: sessionId,
      property_id: propertyId,
      event_type: eventType,
    };

    if (!sessionMetaSent) {
      body.session_meta = getSessionMeta();
      sessionMetaSent = true;
    }

    await supabase.functions.invoke('track-event', { body });
  } catch {
    // silent fail — analytics should never break the UX
  }
}

// --- CORREÇÃO 4: sendBeacon para eventos que precedem navegação externa ---
async function trackEventWithBeacon(propertyId: number, eventType: string): Promise<void> {
  const sessionId = getOrCreateSessionId();

  const bodyPayload: any = {
    session_id: sessionId,
    property_id: propertyId,
    event_type: eventType,
  };

  if (!sessionMetaSent) {
    bodyPayload.session_meta = getSessionMeta();
    sessionMetaSent = true;
  }

  const payload = JSON.stringify(bodyPayload);
  const functionUrl = `${functionsBaseUrl}/track-event`;

  // Tenta sendBeacon primeiro (não bloqueia e sobrevive a navegação)
  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' });
    const sent = navigator.sendBeacon(functionUrl, blob);
    if (sent) return;
  }

  // Fallback: fetch com keepalive (também sobrevive a navegação na maioria dos navegadores)
  try {
    await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
      },
      body: payload,
      keepalive: true,
    });

    return;
  } catch {
    // Último fallback: invoke normal (pode falhar em navegação, mas não quebra UX)
    try {
      await supabase.functions.invoke('track-event', { body: bodyPayload });
    } catch {
      // silent
    }
  }
}

// --- CORREÇÃO 2 & 4: trackLead sem propertyId obrigatório ---
// propertyId é opcional. Quando undefined, o lead é geral (sem imóvel associado).
async function trackLeadInternal(propertyId?: number): Promise<void> {
  const sessionId = getOrCreateSessionId();

  try {
    const body: any = {
      session_id: sessionId,
      event_type: 'lead',
    };

    if (propertyId !== undefined && propertyId > 0) {
      body.property_id = propertyId;
    }
    // Quando propertyId é undefined ou <= 0, não envia property_id
    // A Edge Function tratará como lead geral

    if (!sessionMetaSent) {
      body.session_meta = getSessionMeta();
      sessionMetaSent = true;
    }

    await supabase.functions.invoke('track-event', { body });
  } catch {
    // silent fail
  }
}

export function trackView(propertyId: string | number): void {
  const id = typeof propertyId === 'string' ? parseInt(propertyId, 10) || 0 : propertyId;
  if (!id) return;
  trackEvent(id, 'view');
}

// CORREÇÃO 4: WhatsApp — usa sendBeacon antes da navegação
export function trackWhatsapp(propertyId: number): void {
  trackEventWithBeacon(propertyId, 'whatsapp');
}

// CORREÇÃO 4: Share — usa sendBeacon antes da navegação
export function trackShare(propertyId: number): void {
  trackEventWithBeacon(propertyId, 'share');
}

export function trackFavorite(propertyId: number): void {
  trackEvent(propertyId, 'favorite');
}

// CORREÇÃO 2: trackLead aceita propertyId opcional
export function trackLead(propertyId?: number): void {
  trackLeadInternal(propertyId);
}

export function getSessionId(): string {
  return getOrCreateSessionId();
}
